import styled from '@emotion/styled'
import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import { ClassName } from 'types/props'

import { formatUnits } from '@/utilities/number'

function nFormatter(num, digits) {
  const si = [
    { value: 1, symbol: '' },
    { value: 1e3, symbol: 'k' },
    { value: 1e6, symbol: 'M' },
    { value: 1e9, symbol: 'G' },
    { value: 1e12, symbol: 'T' },
    { value: 1e15, symbol: 'P' },
    { value: 1e18, symbol: 'E' }
  ]
  const rx = /\.0+$|(\.[0-9]*[1-9])0+$/
  let i
  for (i = si.length - 1; i > 0; i--) {
    if (num >= si[i].value) {
      break
    }
  }
  return (num / si[i].value).toFixed(digits).replace(rx, '$1') + si[i].symbol
}

const baseOptions = {
  chart: {
    height: 260,
    zoomType: 'x'
  },
  title: {
    text: ''
  },
  yAxis: {
    opposite: true,
    labels: {
      formatter() {
        return nFormatter(this.value, 1)
      }
    }
  },
  xAxis: {
    type: 'datetime',
    labels: {
      formatter() {
        return Highcharts.dateFormat("%e %b '%y", this.value)
      }
    },
    title: {
      text: 'Date'
    },
    marker: {
      enabled: true
    }
  },
  legend: {
    enabled: false
  },
  tooltip: {
    borderColor: 'var(--Graphite)',
    formatter() {
      return (
        `<span>${Highcharts.dateFormat('%e %b %Y', this.x)}</span><br/>` +
        `<span>${formatUnits(this.y)}</span>`
      )
    }
  },
  plotOptions: {
    area: {
      fillColor: {
        linearGradient: {
          x1: 0,
          y1: 0,
          x2: 0,
          y2: 1
        },
        stops: [
          [0, '#2d232317'],
          [1, '#2d232308']
        ]
      },
      marker: {
        radius: 2
      },
      lineWidth: 1,
      states: {
        hover: {
          lineWidth: 1
        }
      },
      threshold: null
    }
  },
  series: [
    {
      type: 'area',
      data: []
    }
  ]
}

export type ChartProps = {
  metrics: Record<string, number>[]
}

function convertToData(items: Record<string, number>[]) {
  const series = items.map(metrics => {
    const data = []
    Object.keys(metrics).forEach(key => {
      const parts = new Date(key)
      data.push([Date.UTC(parts.getFullYear(), parts.getMonth(), parts.getDate()), metrics[key]])
    })

    return {
      type: 'area',
      data
    }
  })

  return series
}

type InnerChartProps = ChartProps & ClassName

// This is a bit convoluted, but it's how to make it work with @emotion/styled
// SEE: https://stackoverflow.com/questions/62078924/how-to-use-styled-from-react-emotion-with-highchartsreact

function ChartInner({ className = '', metrics }: InnerChartProps) {
  const series = convertToData(metrics)
  const options = {
    ...baseOptions,
    series
  }

  return (
    <HighchartsReact containerProps={{ className }} highcharts={Highcharts} options={options} />
  )
}

export const Chart = styled(ChartInner)<ChartProps>`
  text.highcharts-axis-title,
  text.highcharts-axis-title,
  text.highcharts-credits {
    display: none;
  }

  g.highcharts-axis.highcharts-xaxis {
    path.highcharts-tick {
      stroke: var(--White95);
    }
  }

  g.highcharts-axis-labels.highcharts-xaxis-labels,
  g.highcharts-axis-labels.highcharts-yaxis-labels {
    text {
      color: var(--White60) !important;
      fill: var(--White60) !important;
    }
  }

  g.highcharts-grid.highcharts-yaxis-grid {
    path.highcharts-grid-line {
      stroke: var(--White95);
      &:first-of-type {
        stroke: var(--White95);
      }
    }
  }

  g.highcharts-series-group {
    path.highcharts-graph {
      stroke: var(--Red-Dark);
      stroke-width: 1.5px;
    }

    g.highcharts-markers {
      display: none;
    }
  }
`
